import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {CacheService} from '../services/cache.service';
import {MatSort, SortDirection} from '@angular/material/sort';
import {AfterViewInit, Directive, OnDestroy, OnInit, ViewChild, inject} from '@angular/core';
import {merge, Subscription} from 'rxjs';
import {tap} from 'rxjs/operators';
import {BaseDatasource, BaseModel, ListFilter, Pagination, SearchFieldGroup} from '@shared/base';
import {BaseApi} from '@shared/base/base-api';
import {MatPaginator} from '@angular/material/paginator';

@Directive()
export abstract class BaseList<M extends BaseModel> implements AfterViewInit, OnInit, OnDestroy {
  dataSource: BaseDatasource<M>;
  filterForm: UntypedFormGroup;
  protected subscriptions = new Subscription();

  @ViewChild(MatSort, {static: true}) sort: MatSort;
  @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;

  protected formBuilder = inject(UntypedFormBuilder);
  private cacheService = inject(CacheService);

  protected abstract listApi: BaseApi<M>;
  protected abstract listCachePrefix: string;
  protected listSearchBy: 'filter' | 'request' = 'request';

  ngOnInit() {
    this.dataSource = new BaseDatasource<M>(this.listApi, this.listSearchBy);
    if (!this.filterForm) {
      this.filterForm = this.cacheService.get(`${this.listCachePrefix}-list-filter`, this.defaultListForm()).item as UntypedFormGroup;
    }

    const pagination: Pagination = this.cacheService.get(`${this.listCachePrefix}-list-pagination`, this.defaultPagination()).item as Pagination;

    this.paginator.pageIndex = pagination.page;
    this.paginator.pageSize = pagination.limit;
    this.sort.active = pagination.orderBy;
    this.sort.direction = <SortDirection> pagination.direction;

    this.loadData();
  }

  abstract defaultListForm(): UntypedFormGroup;

  abstract defaultPagination(): Pagination;

  abstract prepareSearch(values: Record<string, unknown>): SearchFieldGroup | ListFilter;

  ngAfterViewInit() {
    this.subscriptions.add(
      merge(this.sort.sortChange, this.paginator.page).pipe(
        tap(() => this.loadData())
      ).subscribe()
    );
  }

  loadData() {
    const pagination: Pagination = this.cacheService.get(`${this.listCachePrefix}-list-pagination`, new Pagination()).item as Pagination;
    pagination.page = this.paginator.pageIndex;
    pagination.limit = this.paginator.pageSize;
    pagination.orderBy = this.sort.active;
    pagination.direction = this.sort.direction;

    this.dataSource.load(this.prepareSearch({...this.filterForm.getRawValue()}), pagination);
  }

  search() {
    this.paginator.pageIndex = 0;
    this.loadData();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
